import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, GraduationCap } from 'lucide-react';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { systemService } from '../../services/systemService';

const Login = () => {
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await systemService.getInstitutionDetails();
                if (res?.success && res?.data?.logo_url) {
                    setLogoUrl(res.data.logo_url);
                }
            } catch (err) {
                console.error("Failed to fetch institution logo", err);
            }
        };
        fetchLogo();
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const onSubmit = async (data) => {
        try {
            setError('');
            setErrorCode('');
            setIsLoading(true);

            const { error: signInError } = await signIn(data.identifier, data.password);

            if (signInError) {
                setErrorCode(signInError.code || '');
                throw signInError;
            }

            navigate('/');
        } catch (err) {
            setErrorCode(err?.code || '');
            setError(err.message || 'Failed to sign in');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
                <div className="text-center">
                    <div className="flex justify-center mb-6">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Institution Logo" className="h-16 w-auto object-contain" />
                        ) : (
                            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                                <GraduationCap className="h-8 w-8 text-blue-600" />
                            </div>
                        )}
                    </div>
                    <h2 className="mt-2 text-3xl font-extrabold text-gray-900">
                        Welcome back
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Please sign in to your account
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    {error && (
                        <div className={`rounded-md border p-4 ${errorCode === 'ACCOUNT_NOT_ACTIVE' ? 'border-amber-200 bg-amber-50' : 'border-red-100 bg-red-50'}`} role="alert">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className={`h-5 w-5 ${errorCode === 'ACCOUNT_NOT_ACTIVE' ? 'text-amber-500' : 'text-red-400'}`} aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <h3 className={`text-sm font-medium ${errorCode === 'ACCOUNT_NOT_ACTIVE' ? 'text-amber-900' : 'text-red-800'}`}>
                                        {errorCode === 'ACCOUNT_NOT_ACTIVE' ? 'Account access unavailable' : 'Sign in failed'}
                                    </h3>
                                    <div className={`mt-2 text-sm ${errorCode === 'ACCOUNT_NOT_ACTIVE' ? 'text-amber-800' : 'text-red-700'}`}>
                                        <p>{error}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <Input
                            label="Email, PF or registration number"
                            type="text"
                            icon={Mail}
                            placeholder="Email, PF number or registration number"
                            error={errors.identifier?.message}
                            {...register('identifier', { required: 'Email, PF number or registration number is required' })}
                        />

                        <div className="space-y-2">
                            <label htmlFor="login-password" className="text-sm font-medium leading-none text-foreground">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                <input
                                    id="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 pl-10 pr-11 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${errors.password ? 'border-destructive focus-visible:ring-destructive' : 'border-input'}`}
                                    {...register('password', { required: 'Password is required' })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((visible) => !visible)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    aria-pressed={showPassword}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                                </button>
                            </div>
                            {errors.password?.message && (
                                <p className="text-sm text-destructive">{errors.password.message}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                                Remember me
                            </label>
                        </div>

                        <div className="text-sm">
                            <Link to="/forgot-password" className="font-medium text-primary hover:text-primary/80">
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full flex justify-center py-2 px-4"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Signing in...
                            </>
                        ) : (
                            'Sign in'
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default Login;
